"use client";

import { Button } from "@prance/ui";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useState } from "react";

type Stage = "login" | "new_password";

/**
 * ログイン画面（PranceIdClientPool・praxi専用App Clientでの認証）。
 * proomi `src/app/login/page.tsx` の最小移植版 — ブランドパネル・SSO連携（proomi固有機能）は
 * 省略し、フォームのみの単一カラムレイアウトにする。
 */
export default function LoginPage() {
  const t = useTranslations("AuthPage");
  const router = useRouter();

  const [stage, setStage] = useState<Stage>("login");
  const [pendingEmail, setPendingEmail] = useState("");
  const [pendingSession, setPendingSession] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleLogin(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const form = e.currentTarget;
    const email = (form.elements.namedItem("email") as HTMLInputElement).value;
    const password = (form.elements.namedItem("password") as HTMLInputElement)
      .value;

    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = (await res.json()) as {
        ok?: boolean;
        challenge?: string;
        session?: string;
        email?: string;
        error?: string;
      };

      if (res.ok && data.challenge === "NEW_PASSWORD_REQUIRED") {
        setPendingEmail(data.email ?? email);
        setPendingSession(data.session ?? "");
        setStage("new_password");
        return;
      }
      if (res.ok) {
        router.push("/");
        router.refresh();
        return;
      }
      setError(
        res.status === 401 ? t("errorInvalidCredentials") : t("errorUnknown"),
      );
    } catch {
      setError(t("errorUnknown"));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleNewPassword(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    const form = e.currentTarget;
    const newPassword = (
      form.elements.namedItem("newPassword") as HTMLInputElement
    ).value;
    const confirmPassword = (
      form.elements.namedItem("confirmPassword") as HTMLInputElement
    ).value;

    if (newPassword !== confirmPassword) {
      setError(t("errorPasswordMismatch"));
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: pendingEmail,
          session: pendingSession,
          newPassword,
        }),
      });
      const data = (await res.json()) as { ok?: boolean; error?: string };

      if (res.ok) {
        router.push("/");
        router.refresh();
        return;
      }
      if (data.error === "session_expired" || res.status === 401) {
        setStage("login");
        setPendingSession("");
        setError(t("errorSessionExpired"));
      } else if (data.error === "invalid_password") {
        setError(t("errorInvalidPassword"));
      } else {
        setError(t("errorUnknown"));
      }
    } catch {
      setError(t("errorUnknown"));
    } finally {
      setSubmitting(false);
    }
  }

  if (stage === "new_password") {
    return (
      <main className="mx-auto flex min-h-screen max-w-sm flex-col justify-center px-6">
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => void handleNewPassword(e)}
        >
          <div>
            <h1 className="mb-2 text-heading">{t("newPasswordTitle")}</h1>
            <p className="text-body text-muted">
              {t("newPasswordDescription")}
            </p>
          </div>
          <div>
            <label
              htmlFor="newPassword"
              className="mb-1 block text-label text-muted"
            >
              {t("newPasswordLabel")}
            </label>
            <input
              id="newPassword"
              name="newPassword"
              type="password"
              required
              minLength={8}
              // biome-ignore lint/a11y/noAutofocus: login form UX
              autoFocus
              autoComplete="new-password"
              className="w-full rounded-lg border border-disabled px-3 py-2 text-body"
            />
          </div>
          <div>
            <label
              htmlFor="confirmPassword"
              className="mb-1 block text-label text-muted"
            >
              {t("confirmPasswordLabel")}
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              className="w-full rounded-lg border border-disabled px-3 py-2 text-body"
            />
          </div>
          {error && <p className="text-error text-label">{error}</p>}
          <Button
            type="submit"
            variant="solid"
            color="primary"
            size="md"
            disabled={submitting}
          >
            {submitting ? t("submitting") : t("newPasswordSubmit")}
          </Button>
        </form>
      </main>
    );
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-sm flex-col justify-center px-6">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => void handleLogin(e)}
      >
        <div>
          <h1 className="mb-2 text-heading">{t("signIn")}</h1>
          <p className="text-body text-muted">{t("signInDescription")}</p>
        </div>

        <div>
          <label htmlFor="email" className="mb-1 block text-label text-muted">
            {t("emailLabel")}
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            // biome-ignore lint/a11y/noAutofocus: login form UX
            autoFocus
            autoComplete="email"
            placeholder={t("emailPlaceholder")}
            className="w-full rounded-lg border border-disabled px-3 py-2 text-body"
          />
        </div>

        <div>
          <label
            htmlFor="password"
            className="mb-1 block text-label text-muted"
          >
            {t("passwordLabel")}
          </label>
          <input
            id="password"
            name="password"
            type="password"
            required
            autoComplete="current-password"
            placeholder={t("passwordPlaceholder")}
            className="w-full rounded-lg border border-disabled px-3 py-2 text-body"
          />
        </div>

        {error && <p className="text-error text-label">{error}</p>}

        <Button
          type="submit"
          variant="solid"
          color="primary"
          size="md"
          disabled={submitting}
        >
          {submitting ? t("submitting") : t("signInButton")}
        </Button>

        <p className="text-center text-caption text-muted">
          {t("contactAdmin")}
        </p>
      </form>
    </main>
  );
}
